import { Component, Inject, Input, OnChanges, OnInit, Optional, SimpleChanges } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { ModalService } from '@app/core/services/modal.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { AcReportLayoutMenuGet, AcReportLayoutService } from '@app/services/ac-report-layout.service';

@Component({
    selector: 'tt-ac-report-layout-menu',
    templateUrl: './ac-report-layout-menu.component.html',
    styleUrls: ['./ac-report-layout-menu.component.css'],
    standalone: false,
})
export class AcReportLayoutMenuComponent implements OnInit, OnChanges {
    @Input() public params?: { acreportlayout_keyno: string };

    public page?: AcReportLayoutMenuGet;

    public grid?: GridOptions;

    public renumbering = false;

    public copying = false;

    public deleting = false;

    constructor(
        private reportLayout: AcReportLayoutService,
        private modal: ModalService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async renumber(): Promise<void> {
        if (!this.page) return;

        this.renumbering = true;
        try {
            const response = await this.reportLayout.renumber(this.page);
            if (response?.errorcode !== '0') {
                await this.showError(response?.errormessage ?? '');
                return;
            }

            this.grid?.gridfunc?.read();
        } finally {
            this.renumbering = false;
        }
    }

    public async copy(): Promise<void> {
        if (!this.page) return;

        this.copying = true;
        try {
            const response = await this.reportLayout.copy(this.page);
            if (response?.errorcode !== '0') {
                await this.showError(response?.errormessage ?? '');
                return;
            }

            await this.navigator?.back();
        } finally {
            this.copying = false;
        }
    }

    public async delete(): Promise<void> {
        if (!this.page) return;

        this.deleting = true;
        try {
            const confirmed = await this.modal.openConfirmDialog({
                type: 'danger',
                title: 'confirm_title',
                translateTitle: true,
                message: 'confirm_message',
                translateMessage: true,
                ok: 'confirm_ok_label',
                cancel: 'confirm_cancel_label',
            });
            if (!confirmed) return;

            const response = await this.reportLayout.delete(this.page);
            if (response && response.errorcode !== '0') {
                await this.showError(response.errormessage);
                return;
            }

            await this.navigator?.back();
        } finally {
            this.deleting = false;
        }
    }

    private async loadPage(): Promise<void> {
        const keyno = this.params?.acreportlayout_keyno;
        if (keyno === undefined || keyno === null || `${keyno}` === '') return;

        this.page = await this.reportLayout.getMenu(keyno);
        this.grid = new Grid()
            .setRememberId('w_acreportlayoutmenu_grid')
            .setLoadDataFunction({ method: 2805, parameters: () => this.page })
            .setKendoConfig({ height: '100%', sortable: true, aggregate: true, pager: false, selectable: false, filterable: false })
            .setCustomToolbarButtons([{
                name: 'acreportlayoutmenu_new',
                text: 'acreportlayoutmenu_new',
                func: () => this.navigator?.go({ to: 'acreportlayoutline', parms: { acreportlayout_keyno: this.page?.acreportlayout_keyno, acreportlayoutline_keyno: '0' } }),
                icon: 'fas fa-plus',
                cssClass: 'tt-button tt-button--primary im-grid-btn-xs-r',
                translate: true,
            }]);
    }

    private showError(message: string): Promise<void> {
        return this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message });
    }

    public ngOnInit(): void {
        if (this.navigator) {
            this.params = this.navigator.params as unknown as { acreportlayout_keyno: string };
            void this.loadPage();
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']?.currentValue) void this.loadPage();
    }
}
